import type { AvailabilityInputs, AvailabilityRule } from './availability'
import type { FgaAuthorizationRule } from './fgaRules'
import type { RoleAuthorizationContext, RoleAuthorizationRule } from './roleAuthorization'
import type { AuthzContextState } from './useAuthzContextQuery'
import type { AnyRecordAuthorization } from './useRecordAuthorization'
import type { RecordPermissionsEntity, RecordPermissionsFor } from '@omnea-digital/authz-model'

/** The verbs of one entity family. Narrowed to string so a generic entity's verb can still index its map. */
export type RecordVerb<Entity extends RecordPermissionsEntity> = keyof RecordPermissionsFor<Entity> & string

/** Construct only through `recordAuthorization`: `verb` is widened here, so a literal skips its entity check. */
type RecordAuthorizationRule = {
  kind: 'record'
  entity: RecordPermissionsEntity
  verb: string
  role: RoleAuthorizationRule
}

// TODO(PLT-2173): delete the switch, keeping each `flagOn` rule, once RELEASE_TPRM_LEVELS is on for every organisation.
/**
 * One rule while RELEASE_TPRM_LEVELS is off for the organisation, another once it is on. `flagOff` is the rule the
 * surface had before, so turning the flag off restores it. The answer is withheld until LaunchDarkly has one.
 */
export type TprmLevelsSwitch<Rule> = { kind: 'tprmLevelsSwitch'; flagOff: Rule; flagOn: Rule }

type OrTprmLevelsSwitch<Rule> = Rule | TprmLevelsSwitch<Rule>

export const tprmLevelsSwitch = <Rule>({ flagOff, flagOn }: { flagOff: Rule; flagOn: Rule }): TprmLevelsSwitch<Rule> => ({
  kind: 'tprmLevelsSwitch',
  flagOff,
  flagOn,
})

type NonRecordAuthorizationRule = OrTprmLevelsSwitch<
  | { kind: 'open' }
  | { kind: 'fga'; fga: FgaAuthorizationRule }
  | { kind: 'fgaOrRole'; fga: FgaAuthorizationRule; role: RoleAuthorizationRule }
  | { kind: 'role'; role: RoleAuthorizationRule }
>

/** Which implementation decides, and what it reads. `open` is still subject to availability. */
export type AuthorizationRule = NonRecordAuthorizationRule | RecordAuthorizationRule

/**
 * Anything gated: whether it exists for the organisation at all, and who may reach it. A route is this
 * plus an address; every other surface is this alone. Availability is omitted where the surface exists
 * for every organisation, which is most of them.
 */
export type AccessRule = {
  availability?: AvailabilityRule
  authorization: AuthorizationRule
}

/** A rule the caller must name a record for; the hooks take that id as a second argument. */
export type RecordAccessRule = { availability?: AvailabilityRule; authorization: RecordAuthorizationRule }

/**
 * What a record page asks of its record. A page of sections that each carry their own rule opens when
 * any one of them would show, so its gate is those rules and cannot drift from them.
 */
export type RecordGate = OrTprmLevelsSwitch<
  RecordAccessRule | { anyOf: readonly [RecordAccessRule, RecordAccessRule, ...RecordAccessRule[]] }
>

/** Every rule in a gate reads the same entity: the flag-on arm names it. */
export const recordGateEntity = (gate: RecordGate): RecordPermissionsEntity => {
  const flagOnGate = 'kind' in gate ? gate.flagOn : gate
  return ('anyOf' in flagOnGate ? flagOnGate.anyOf[0] : flagOnGate).authorization.entity
}

/** A rule that answers without a record, so asking one for a record id would be meaningless. */
export type NonRecordAccessRule = {
  availability?: AvailabilityRule
  authorization: NonRecordAuthorizationRule
}

/**
 * `pending` and `unavailable` are withheld answers rather than denials: a caller who has access must
 * not be redirected because a check had not finished or could not be made.
 */
export type AccessAnswer = 'allowed' | 'denied' | 'pending' | 'unavailable'

/** Every source, in the state the hooks hold them. */
export type AccessSources = {
  authz: AuthzContextState
  role: RoleAuthorizationContext
  availability: AvailabilityInputs
  record?: AnyRecordAuthorization
}

/**
 * One verb of one entity family. The rule names no record, so every site asking this question shares
 * it and the caller supplies the record id. The role arm decides where the service does not answer
 * for the record.
 */
export const recordAuthorization = <Entity extends RecordPermissionsEntity>(
  entity: Entity,
  verb: RecordVerb<Entity>,
  role: RoleAuthorizationRule
): RecordAuthorizationRule => ({ kind: 'record', entity, verb, role })
